/**
 * Coach-facing copy for the tolerance review (observability Phase 4).
 *
 * The problem this surface exists for: every angle check carries a number of
 * degrees that decides how far from the demo still counts as right, and almost
 * no coach has ever chosen one — the client shipped a preset equal to the
 * model default, so 29 of the 41 live checks sit exactly on it (study metric
 * M8, ledger row HC-26). Phase 4 asks the coach.
 *
 * SINGLE SOURCE of every string the studio's strictness control and the
 * template page's review panel render, so the copy gate in
 * `docs/COACH_FACING_COPY_STANDARD.md` §4 has one place to review and the
 * components stay layout-only.
 *
 * Every string answers the standard's three questions, in order:
 *   1. WHAT happened / what to do
 *   2. WHY, in one sentence a coach would say to another coach
 *   3. HOW — a concrete instruction, or "nothing to do"
 *
 * and uses no internal vocabulary. Pinned by
 * `frontend/src/test/coachCopyDenylist.ts`.
 *
 * DELIBERATELY NOT HERE: strict / normal / relaxed labels. The §3 draft of the
 * standard sketched them, but each would be a new number with nothing behind
 * it — exactly the class-D constant ledger row HC-26 exists to retire (and the
 * preset trio the joint-angle editor used to show, 25 / 15 / 8, is HC-30 and
 * the direct cause of M8's 29). The coach types the degrees they would accept,
 * or keeps the standard setting. A named band comes back only when calibration
 * mints one from the coach's own takes.
 */

/** "1 degree" reads wrong as "1 degrees"; everything else is plural. */
function degreesPhrase(degrees: number): string {
  const rounded = Math.round(degrees * 10) / 10;
  return `${rounded} ${rounded === 1 ? 'degree' : 'degrees'}`;
}

/**
 * What the check's degrees are measured AGAINST. A check can follow the coach's
 * own demo, or an angle they typed in; saying "of your demo" for the second
 * would be plainly wrong to the coach reading it, so the caller declares which.
 * `'unspecified'` is for surfaces that do not know (the template page's review
 * panel reads a summary that carries no reference) — it drops the clause rather
 * than guessing.
 */
export type StrictnessReference = 'demo' | 'target' | 'unspecified';

/**
 * How the current setting is described wherever a value is shown.
 * "within 10 degrees of your demo" — the thing the coach can act on.
 */
export function strictnessValueText(
  degrees: number,
  comparedTo: StrictnessReference = 'demo',
): string {
  const amount = `within ${degreesPhrase(degrees)}`;
  if (comparedTo === 'demo') return `${amount} of your demo`;
  if (comparedTo === 'target') return `${amount} of the angle you set`;
  return amount;
}

export interface ThreeSentences {
  what: string;
  why: string;
  how: string;
}

/**
 * The prompt for a check still on the standard setting — the owner-approved
 * wording of `docs/COACH_FACING_COPY_STANDARD.md` §3, with the HOW rewritten
 * to drop the preset names (see the module note above).
 */
export function standardStrictnessPrompt(label: string): ThreeSentences {
  return {
    what: 'Set how strict this check should be.',
    why:
      'Right now it uses a standard setting that may be stricter or looser ' +
      "than you'd coach it.",
    how: `Type the number of degrees you'd accept for ‘${label}’, or keep the standard setting.`,
  };
}

/**
 * The confirmation for a check the coach has already set. WHAT / WHY / HOW
 * still, with "nothing to do" as the HOW — §1 allows that, and a coach who
 * already answered this question should not be asked again.
 */
export function coachSetStrictnessNote(
  degrees: number,
  comparedTo: StrictnessReference = 'demo',
): ThreeSentences {
  return {
    what: `You set how strict this check is: ${strictnessValueText(degrees, comparedTo)}.`,
    why: "Scores for this check are measured against your number, not the standard setting.",
    how: 'Nothing to do. Change the number any time, or go back to the standard setting.',
  };
}

/** The label on the degrees field, and its spoken name for screen readers. */
export const STRICTNESS_FIELD_LABEL = "Degrees you'd accept";

/** The action that puts a check back on the standard setting. */
export const USE_STANDARD_SETTING_ACTION = 'Use the standard setting';

/** Shown next to the current value when the standard setting is in force. */
export const STANDARD_SETTING_BADGE = 'Standard setting';

/** Shown next to the current value when the coach chose it. */
export const COACH_SET_BADGE = 'You set this';

// ---------------------------------------------------------------------------
// Template page review panel (coach)
// ---------------------------------------------------------------------------

/**
 * The panel heading. Both numbers come from the backend's finalize-time
 * summary (`metadata.tolerance_review.on_default` / the row count) — never
 * from comparing a check's degrees against a default the client guessed at.
 */
export function toleranceReviewHeadline(onDefault: number, total: number): string {
  return `Checks still on the standard strictness: ${onDefault} of ${total}`;
}

export function toleranceReviewPanelCopy(): ThreeSentences {
  return {
    what: 'Decide how strict each of these checks should be.',
    why:
      'They use a standard setting that may be stricter or looser than ' +
      "you'd coach it, so an athlete can lose marks you would have given.",
    how:
      "Open the template editor and type the number of degrees you'd accept " +
      'for each one, or keep the standard setting.',
  };
}

/**
 * One row per check still on the standard setting. The summary carries no
 * reference, so the row states the amount without claiming what it is measured
 * against — the editor, which knows, says that.
 */
export function toleranceReviewRowText(label: string, degrees: number | null): string {
  return degrees === null
    ? `‘${label}’`
    : `‘${label}’ — ${strictnessValueText(degrees, 'unspecified')}`;
}

/** The link into the studio, where the number is actually changed. */
export const TOLERANCE_REVIEW_LINK_TEXT = 'Review these checks';
